import React from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { fade, makeStyles } from '@material-ui/core/styles';
import { t } from 'ttag';
import Popper from '@material-ui/core/Popper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import ListIcon from '@material-ui/icons/List';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { Archive, Edit } from '@material-ui/icons';
import useApi from '../../api';
import DataTable from '../DataTable';
import { calculateAge, formatPublicDate } from '../../utils/helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    fontSize: 13,
  },
  button: {
    fontSize: 13,
    width: '100%',
    textAlign: 'left',
    paddingBottom: 8,
    color: '#586069',
    fontWeight: 600,
    '&:hover,&:focus': {
      color: '#0366d6',
    },
    '& span': {
      width: '100%',
    },
    '& svg': {
      width: 16,
      height: 16,
    },
  },
  tag: {
    marginTop: 3,
    height: 20,
    padding: '.15em 4px',
    fontWeight: 600,
    lineHeight: '15px',
    borderRadius: 2,
  },
  popper: {
    marginTop: '100px',
    border: '1px solid rgba(27,31,35,.15)',
    boxShadow: '0 3px 12px rgba(27,31,35,.15)',
    borderRadius: 3,
    width: '40%',
    zIndex: 100,
    fontSize: 13,
    color: '#586069',
    backgroundColor: '#f6f8fa',
  },
  header: {
    borderBottom: '1px solid #e1e4e8',
    padding: '8px 10px',
    fontWeight: 600,
  },
  inputBase: {
    padding: 10,
    width: '100%',
    borderBottom: '1px solid #dfe2e5',
    '& input': {
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      padding: 8,
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      border: '1px solid #ced4da',
      fontSize: 14,
      '&:focus': {
        boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  paper: {
    boxShadow: 'none',
    margin: 0,
    color: '#586069',
    fontSize: 13,
  },
  option: {
    minHeight: 'auto',
    alignItems: 'center',
    padding: 8,
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[data-focus="true"]': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  popperDisablePortal: {
    position: 'relative',
  },
  iconSelected: {
    width: 17,
    height: 17,
    marginRight: 5,
    marginLeft: -2,
  },
  color: {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: 3,
    marginRight: 8,
    marginTop: 2,
  },
  text: {
    flexGrow: 2,
  },
  close: {
    opacity: 0.6,
    width: 18,
    height: 18,
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -12,
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
}));

export default function SelectChooser({
  name,
  value,
  onChange,
  submitForm,
  canSubmit,
  dataKey,
  valueKey,
  query,
  onArvitate,
  variables,
  disabled,
  onView,
  onEdit,
  groupId,
  trainerId,
  section,
}) {
  const api = useApi();
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [pendingValue, setPendingValue] = React.useState(value);

  const selectedDataMap = pendingValue.reduce((acc, curr) => {
    acc[curr[valueKey]] = curr;
    return acc;
  }, {});
  const onCompleted = (d) => {
    const res = d[dataKey].map((r) => {
      r.tableData = { checked: !!selectedDataMap[r.id] };
      return r;
    });
    setOptions(res);
    setLoading(false);
    setOpen(true);
  };

  const [getQuery] = useLazyQuery(query, {
    fetchPolicy: 'no-cache',
    variables,
    onCompleted,
  });

  const handleClick = (event) => {
    setLoading(true);
    setAnchorEl(event.currentTarget);
    getQuery({
      fetchPolicy: 'no-cache',
      variables,
      onCompleted,
    });
  };

  const handleAccept = () => {
    onChange({ target: { name, value: pendingValue } });
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
    setOpen(false);
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
    setOpen(false);
  };

  const id = open ? 'github-label' : undefined;

  const obj = {
    group_id: groupId,
    trainer_id: trainerId,
    section: section,
  }
  const urlParams = Object.entries(obj)
    .map(pair => pair.map(encodeURIComponent).join('=')).filter(v => !!v)
    .join('&');

  return (
    <>
      <div>
        <div className={classes.wrapper}>
          <Grid container spacing={6}>
            <Grid item>
              <Link
                target='_blank'
                href={`${api.router.athletes.create()}?${urlParams}&next=${api.router.groups.edit(
                  groupId
                )}`}
                variant='body2'
              >
                <Button
                  disabled={disabled || loading}
                  style={{ marginBottom: '20px' }}
                  margin='normal'
                  type='button'
                  fullWidth
                  variant='outlined'
                  color='primary'
                  startIcon={<AddIcon />}
                >
                  {t`Додати нового спортсмена`}
                </Button>
              </Link>
            </Grid>
            <Grid item>
              <Button
                disabled={disabled || loading}
                style={{ marginBottom: '20px' }}
                margin='normal'
                type='button'
                fullWidth
                variant='outlined'
                color='primary'
                startIcon={<ListIcon />}
                onClick={handleClick}
              >
                {t`Додати спортсмена зі списку`}
              </Button>
            </Grid>
          </Grid>
          {loading && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </div>
      </div>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement='bottom'
        className={classes.popper}
      >
        <div className={classes.header}>
          {t`Виберіть спортсменів або `}
          <Link
            target='_blank'
            href={`${api.router.athletes.create()}?${urlParams}&next=${api.router.groups.edit(
              groupId
            )}`}
            variant='body2'
          >
            {t`додати нового`}
          </Link>
        </div>
        <DataTable
          title=''
          selection
          pageSize={Math.min(10, 20)}
          columns={[
            { title: t`Id`, field: 'id', editable: 'never' },
            { title: t`Ім'я`, field: 'fullName' },
            {
              title: t`Дата народження`,
              field: 'birthday',
              render: (r) =>
                `${formatPublicDate(r.birthday)} (${calculateAge(r.birthday)})`,
            },
            {
              title: t`Дії`,
              field: 'actions',
              render: (r, rowData) => [
                <VisibilityIcon
                  key={r.id}
                  onClick={() => {
                    onView(r);
                  }}
                />,
                <Archive
                  key={r.id}
                  onClick={() => {
                    onArvitate(rowData, r);
                  }}
                />,
                <Edit
                  key={r.id}
                  onClick={() => {
                    onEdit(rowData, r);
                  }}
                />,
              ],
            },
          ]}
          data={options}
          onSelectionChange={(rows) => setPendingValue(rows)}
          selectionProps={(row) => ({
            checked: !!selectedDataMap[row.id],
          })}
        />
        <div className={classes.header}>
          <Grid container spacing={6}>
            <Grid item>
              <Button
                disabled={!canSubmit || disabled || loading}
                style={{ marginBottom: '20px' }}
                margin='normal'
                type='button'
                fullWidth
                size='small'
                variant='contained'
                color='primary'
                onClick={() => {
                  handleAccept();
                  submitForm();
                }}
              >
                {t`Застосувати і зберегти`}
              </Button>
            </Grid>
            <Grid item>
              <Button
                disabled={disabled || loading}
                style={{ marginBottom: '20px' }}
                margin='normal'
                type='button'
                fullWidth
                size='small'
                variant='contained'
                color='primary'
                onClick={handleAccept}
              >
                {t`Застосувати`}
              </Button>
            </Grid>
            <Grid item>
              <Button
                disabled={disabled || loading}
                style={{ marginBottom: '20px' }}
                margin='normal'
                type='button'
                fullWidth
                variant='outlined'
                color='primary'
                size='small'
                onClick={handleClose}
              >
                {t`Відмінити`}
              </Button>
            </Grid>
          </Grid>
        </div>
      </Popper>
    </>
  );
}
